import useIsEmea from "@dana/Common/Hooks/useIsEmea";
import AnnualReturnsRMADetailsView from "@dana/Components/AnnualReturnsOrderStatus/AnnualReturnsRMADetailsView";
import getStandardReturnOrderDetails from "@dana/Store/Data/StandardReturnsStatus/Handlers/StandardReturnOrderDetailsHandler";
import openPrintDialog from "@insite/client-framework/Common/Utilities/openPrintDialog";
import { FulfillmentMethod } from "@insite/client-framework/Services/SessionService";
import siteMessage from "@insite/client-framework/SiteMessage";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getBillToState } from "@insite/client-framework/Store/Data/BillTos/BillTosSelectors";
import { getCartState } from "@insite/client-framework/Store/Data/Carts/CartsSelector";
import { getHomePageUrl } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import LocalizedDateTime from "@insite/content-library/Components/LocalizedDateTime";
import Button, { ButtonPresentationProps } from "@insite/mobius/Button";
import GridContainer, { GridContainerProps } from "@insite/mobius/GridContainer";
import GridItem, { GridItemProps } from "@insite/mobius/GridItem";
import User from "@insite/mobius/Icons/User";
import Link from "@insite/mobius/Link";
import Modal, { ModalPresentationProps } from "@insite/mobius/Modal";
import Typography, { TypographyPresentationProps } from "@insite/mobius/Typography";
import getColor from "@insite/mobius/utilities/getColor";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect, ResolveThunks } from "react-redux";
import styled, { css } from "styled-components";

const mapStateToProps = (state: ApplicationState) => {
    const cart = getCartState(state, state.pages.onePageCheckoutConfirmation.cartId)?.value;
    return {
        cart,
        billTo: getBillToState(state, cart?.billToId)?.value,
        homePageUrl: getHomePageUrl(state),
        username: state.context.session?.userName,
        isEmea: useIsEmea(state),
    };
};

const mapDispatchToProps = {
    getStandardReturnOrderDetails,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface OnePageCheckoutConfirmationHeaderStyles {
    container?: GridContainerProps;
    titleGridItem?: GridItemProps;
    titleText?: TypographyPresentationProps;
    emailSentText?: TypographyPresentationProps;
    divider?: InjectableCss;
    orderInfoGridItem?: GridItemProps;
    orderNumberText?: TypographyPresentationProps;
    orderPlacedText?: TypographyPresentationProps;
    deliveryMethodText?: TypographyPresentationProps;
    buttonsGridItem?: GridItemProps;
    printButton?: ButtonPresentationProps;
    viewRmaModal?: ModalPresentationProps;
}

export const headerStyles: OnePageCheckoutConfirmationHeaderStyles = {
    titleGridItem: {
        width: 12,
        css: css`
            flex-direction: column;
        `,
    },
    titleText: {
        variant: "h2",
    },
    divider: {
        css: css`
            padding: 15px;
            height: 100%;
            width: 100%;
            overflow: hidden;
            border-bottom: 1px solid ${getColor("common.border")};
        `,
    },
    orderInfoGridItem: {
        width: [8, 8, 10, 10, 10],
        css: css`
            flex-direction: column;
        `,
    },
    deliveryMethodText: {
        css: css`
            margin-top: 10px;
        `,
    },
    orderNumberText: { variant: "h5" },
    buttonsGridItem: {
        width: [4, 4, 2, 2, 2],
        css: css`
            justify-content: flex-end;
        `,
    },
    printButton: {
        buttonType: "outline",
        variant: "secondary",
    },
    viewRmaModal: {
        size: 1140,
        sizeVariant: "large",
        headlineTypographyProps: {
            variant: "h4",
            css: css`
                        margin-bottom: 0;
                    `,
        },
    },
};

const styles = headerStyles;

const Divider = styled.div`
    ${styles.divider?.css}
`;

const OnePageCheckoutConfirmationHeader = ({ cart, billTo, username, getStandardReturnOrderDetails,isEmea}: Props) => {

    const [isRmaModalOpen, setRmaModalOpen] = useState<boolean>(false);
    const [rmaModalTitle, setRmaModalTitle] = useState<string>("");

    const printOrOpenPrintAllModal = () => {
        openPrintDialog();
    };

    if (!cart || !billTo) {
        return null;
    }

    const getAnnualReturnsRMAHandler = () => {
        const orderNumber = cart?.erpOrderNumber;
        if (!orderNumber) {
            return;
        }
        setRmaModalOpen(true);
        setRmaModalTitle(`RMA for ${orderNumber}`);
        getStandardReturnOrderDetails({ orderNumber, additionalExpands: ['rma'] })
    }

    return (
        <GridContainer {...styles.container}>
            <GridItem {...styles.titleGridItem}>
                <Typography as="h2" {...styles.titleText} data-test-selector="orderConfirmation_Success">
                    {siteMessage("OrderConfirmation_Success")}
                </Typography>
                <Typography as="p" {...styles.emailSentText} data-test-selector="orderConfirmation_EmailSent">
                    {siteMessage(
                        "OrderConfirmation_EmailSent",
                        `${username}${billTo.email ? `,${billTo.email}` : ""}${cart.additionalEmails ? `,${cart.additionalEmails}` : ""
                        }`,
                    )}
                </Typography>
                <Divider />
            </GridItem>
            <GridItem {...styles.orderInfoGridItem}>
                <Typography as="h3" {...styles.orderNumberText} data-test-selector="orderConfirmation_orderNumber">
                    {translate("Process Number")} - {cart.orderNumber}
                </Typography>
                {cart.erpOrderNumber !== "" && (
                    <Typography as="h3" {...styles.orderNumberText} data-test-selector="orderConfirmation_orderNumber">
                        {translate("Order Number")} - {cart.erpOrderNumber}
                    </Typography>
                )}
                <Typography as="p" {...styles.orderPlacedText} data-test-selector="orderConfirmation_orderPlaced">
                    <strong>{translate("Order placed")}:&nbsp;</strong>
                    <LocalizedDateTime
                        dateTime={cart.orderDate}
                        options={{ year: "numeric", month: "numeric", day: "numeric" }}
                    />
                </Typography>
                {isEmea===false && cart.properties?.isEmergencyOrder === "True" && (cart.carrier || cart.shipVia) && (
                    <Typography
                        as="p"
                        {...styles.deliveryMethodText}
                        data-test-selector="orderConfirmation_deliveryMethod"
                    >
                        <strong>{translate("Shipping Method")}:&nbsp;</strong>
                        {(() => {
                            const c = cart.carrier?.description?.replace("♦", "") || "";
                            const s = cart.shipVia?.description || "";

                            if (!c && !s) { return ""; }
                            if (c && s && c === s) { return c; }
                            return [c, s].filter(Boolean).join(" - ");
                        })()}
                    </Typography>
                )}
                {cart.properties?.isEmergencyOrder === "True" &&
                    cart.properties?.thirdPartycarrieraccountnumber !== undefined &&
                    cart.properties?.thirdPartycarrieraccountnumber !== "" && (
                        <Typography
                            as="p"
                            {...styles.deliveryMethodText}
                            data-test-selector="orderConfirmation_deliveryMethodNotes"
                        >
                            <strong>{translate("Carrier Account No.")}:&nbsp;</strong>
                            {cart.properties?.thirdPartycarrieraccountnumber}
                        </Typography>
                    )}
                {cart.notes !== undefined && cart.notes !== "" && (
                    <Typography
                        as="p"
                        {...styles.deliveryMethodText}
                        data-test-selector="orderConfirmation_deliveryMethodNotes"
                    >
                        <strong>{translate("Specify Shipping Notes")}:&nbsp;</strong>
                        {cart.notes}
                    </Typography>
                )}
            </GridItem>
            <GridItem {...styles.buttonsGridItem}>
                {cart.properties?.isAnnualReturnsOrder === "True" ? (
                    <>
                        <Link className="btn btn-secondary" href="/MyAccount/Returns/AnnualReturns/ReturnOrderStatus">
                            {translate("Return Order Status")}
                        </Link>
                        <Button
                            {...styles.printButton}
                            onClick={getAnnualReturnsRMAHandler}
                            data-test-selector="orderConfirmation_viewRMAButton"
                        >
                            {translate("View RMA")}
                        </Button>
                    </>
                ) : (
                    <>
                        <Link className="btn btn-secondary" href="/">
                            {translate("Continue Shopping")}
                        </Link>
                        <Button
                            {...styles.printButton}
                            onClick={printOrOpenPrintAllModal}
                            data-test-selector="orderConfirmation_printButton"
                        >
                            {translate("Print")}
                        </Button>
                    </>
                )}
            </GridItem>
            <Modal headline={translate(rmaModalTitle)} isOpen={isRmaModalOpen} handleClose={() => { setRmaModalOpen(false); }} {...styles.viewRmaModal}>
                <AnnualReturnsRMADetailsView />
            </Modal>
        </GridContainer>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(OnePageCheckoutConfirmationHeader),
    definition: {
        displayName: "Header",
        group: "One Page Checkout Confirmation",
        allowedContexts: ["OnePageCheckoutConfirmationPage"],
    },
};

export default widgetModule;
